import { IDunningCreateSchema, IDunnings } from '@monetize/types/app';
import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apiGet, apiPost, handleApiErrorToast } from '../api/axios';
import { settingsQueryKeys } from '../api/settingsService';
import { convertDunningRequestFromResponse } from '../routes/Settings/Billing/Dunning/dunning.utils';

export function useCloneDunning(
  options: Partial<UseMutationOptions<IDunnings, unknown, string>> = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: async (dunningId: string) => {
      const response = await apiGet<IDunningCreateSchema>(
        `/api/dunning/dunningProcess/${dunningId}`,
      );
      const dunningToClone = response.data;

      const clonedDunning: IDunningCreateSchema = {
        name: `Clone - ${dunningToClone.name}`,
        dunningSteps: dunningToClone.dunningSteps.map((step) => {
          const { id, ...rest } = convertDunningRequestFromResponse(step);
          return rest;
        }),
      };

      const createResponse = await apiPost<IDunnings>(
        '/api/dunning/dunningProcess',
        clonedDunning,
      );
      const createdDunning = createResponse.data;

      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.dunningList(),
      });

      if (createdDunning.id) {
        queryClient.setQueryData(
          [...settingsQueryKeys.dunningDetail(createdDunning.id)],
          createdDunning,
        );
      }

      return createdDunning;
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      handleApiErrorToast(error);
      restOptions.onError?.(error, variables, context);
    },
    ...restOptions,
  });
}
